import { computed, ref, shallowRef } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useScheduleStore } from '@/stores/scheduleStore'
import { EXCHANGE_MOCK_ENABLED } from './exchangeApi'
import type { NamedRef } from './exchangeTypes'

interface MockDirectory {
    profileDepartment: NamedRef
    positions: NamedRef[]
    departments: NamedRef[]
}

const mockDirectory = shallowRef<MockDirectory | null>(null)
let mockLoading: Promise<void> | null = null

function loadMockDirectory() {
    if (!EXCHANGE_MOCK_ENABLED) return
    mockLoading ??= import('./mock/exchangeMockScenario').then(module => { mockDirectory.value = module.mockDirectory })
}

/**
 * Справочники биржи: магазин профиля, должности, магазины для запроса.
 * Реальный режим — существующие сторы; демо-сценарий — его синтетические записи (без смешения ID).
 */
export function useExchangeDirectory() {
    const authStore = useAuthStore()
    const scheduleStore = useScheduleStore()
    const departmentStore = useDepartmentStore()
    loadMockDirectory()

    const profileDepartment = computed<NamedRef | null>(() => {
        if (EXCHANGE_MOCK_ENABLED) return mockDirectory.value?.profileDepartment ?? null
        const department = authStore.userInfo?.department
        return department?.id ? { id: department.id, name: department.name ?? '' } : null
    })

    const positions = computed<NamedRef[]>(() => {
        if (EXCHANGE_MOCK_ENABLED) return mockDirectory.value?.positions ?? []
        return scheduleStore.positions.map(({ id, name }) => ({ id, name }))
    })

    const departmentsSearch = ref('')
    const departments = computed<NamedRef[]>(() => {
        const own = profileDepartment.value?.id
        const list = EXCHANGE_MOCK_ENABLED
            ? (mockDirectory.value?.departments ?? []).filter(item => item.name.toLowerCase().includes(departmentsSearch.value.trim().toLowerCase()))
            : departmentStore.departments.map((item: NamedRef) => ({ id: item.id, name: item.name }))
        return list.filter(item => item.id !== own)
    })

    async function searchDepartments(text: string) {
        departmentsSearch.value = text
        if (EXCHANGE_MOCK_ENABLED) return
        await departmentStore.fetchDepartments(text ? { search: text } : undefined)
    }

    return { profileDepartment, positions, departments, searchDepartments }
}
